import React from 'react';
import { useNavigate } from 'react-router-dom';
import { services } from '../mock';
import { Target, TrendingUp, ShoppingBag, Shield, ArrowRightCircle, Check, Package } from 'lucide-react';
import { Button } from '../components/ui/button';

const iconMap = {
  Target,
  TrendingUp,
  ShoppingBag,
  Shield,
  ArrowRightCircle
};

export const Services = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#1e1919' }}>
              Services
            </h1>
            <p className="text-xl" style={{ color: '#736c64' }}>
              Operator-level intervention for businesses that need execution, not advice.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <div key={service.id} className="bg-white p-8 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="p-3 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: '#f7f5f2' }}
                    >
                      <Icon className="h-6 w-6" style={{ color: '#059669' }} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#1e1919' }}>
                        {service.title}
                      </h3>
                      <p className="mb-4" style={{ color: '#736c64' }}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <div className="pl-16">
                    <p className="text-sm font-semibold mb-3" style={{ color: '#059669' }}>
                      What you get:
                    </p>
                    <ul className="space-y-2">
                      {service.outcomes.map((outcome, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#736c64' }}>
                          <Check className="h-4 w-4 flex-shrink-0" style={{ color: '#059669' }} />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Sourcing Deep Dive */}
      <section className="py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div
                className="p-4 rounded-lg"
                style={{ backgroundColor: 'white' }}
              >
                <Package className="h-8 w-8" style={{ color: '#059669' }} />
              </div>
              <h2 className="text-4xl font-bold" style={{ color: '#1e1919' }}>
                Product Sourcing & Stock Solutions
              </h2>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200 mb-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e1919' }}>
                Global Sourcing Network
              </h3>
              <p className="text-lg mb-6" style={{ color: '#736c64' }}>
                13+ years building relationships with suppliers, telcos, and wholesale networks across EU, USA, Dubai, Singapore, China, Japan, and Australia. This is not a contact list. This is direct access to verified sources.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: '#059669' }}>
                    Direct Telco Partnerships
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: '#736c64' }}>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      Trade-in program access
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      Bulk device procurement
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      Stock financing arrangements
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      Priority allocation during shortages
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-3" style={{ color: '#059669' }}>
                    Wholesale Networks
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: '#736c64' }}>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      B2B relationships across 6+ countries
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      Verified supplier vetting
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      Quality control protocols
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                      Payment terms negotiation
                    </li>
                  </ul>
                </div>
              </div>

              <h4 className="font-semibold mb-3" style={{ color: '#059669' }}>
                What This Means For You
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                  <div>
                    <span className="font-semibold" style={{ color: '#1e1919' }}>Better pricing:</span>
                    <span style={{ color: '#736c64' }}> Direct relationships mean lower costs</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                  <div>
                    <span className="font-semibold" style={{ color: '#1e1919' }}>Reliable supply:</span>
                    <span style={{ color: '#736c64' }}> Access during market shortages</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                  <div>
                    <span className="font-semibold" style={{ color: '#1e1919' }}>Quality assurance:</span>
                    <span style={{ color: '#736c64' }}> Tested grading and verification processes</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                  <div>
                    <span className="font-semibold" style={{ color: '#1e1919' }}>Faster deployment:</span>
                    <span style={{ color: '#736c64' }}> Skip months of relationship building</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e1919' }}>
                Logistics & Shipping Optimization
              </h3>
              <p className="text-lg mb-6" style={{ color: '#736c64' }}>
                Established partnerships with DHL and UPS. Access to negotiated rates and insurance solutions that can significantly reduce your shipping costs.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
                  <h4 className="font-semibold mb-2" style={{ color: '#1e1919' }}>Shipping Rates</h4>
                  <p className="text-sm" style={{ color: '#736c64' }}>Leverage volume discounts and carrier relationships for better pricing</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
                  <h4 className="font-semibold mb-2" style={{ color: '#1e1919' }}>Insurance Solutions</h4>
                  <p className="text-sm" style={{ color: '#736c64' }}>Proper coverage at competitive rates to protect high-value shipments</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6" style={{ color: '#1e1919' }}>
            Ready to Fix What's Broken?
          </h2>
          <p className="text-xl mb-8" style={{ color: '#736c64' }}>
            No hourly billing. No endless calls. Clear outcomes only.
          </p>
          <Button
            onClick={scrollToContact}
            size="lg"
            className="text-lg px-8"
            style={{ backgroundColor: '#059669', color: 'white' }}
          >
            Work With Us
          </Button>
        </div>
      </section>
    </div>
  );
};